import * as React from 'react';
import classnames from 'classnames';

import {Text, TextProps} from '../../web';
import {usePrivateHeadingLevel} from './HeadingLevel.web';

import styles from './Heading.module.css';

export interface HeadingProps extends Omit<TextProps, 'tag' | 'variant'> {
  /**
   * Apply a named visual style to the heading. These match the [text styles found in Figma][text-styles].
   *
   * [text-styles]: https://www.figma.com/file/TBUKIi4YR9E2r6AxZxViZEh5/Tokens?node-id=12491%3A946
   */
  variant: TextProps['variant'];

  /**
   * This prop previously decided the HTML tag used for the heading (h1-h6), but it was removed in favor of using the
   * `HeadingLevel` component to automatically increment heading levels.
   *
   * See `HeadingLevel.tsx` for usage instructions if needed. In most cases, adding a `HeadingLevel` is not needed.
   *
   * @deprecated
   */
  level?: never;
}

/**
 * The Heading component is used to apply one of our [text styles found in Figma][figma] to a heading. Headings have
 * special meaning in applications and are used to provide hierarchical landmarks, especially for screen reader users.
 *
 * Headings work in tandem with the `HeadingLevel` component to automatically determine the proper tag (h1-h6).
 *
 * [figma]: https://www.figma.com/file/TBUKIi4YR9E2r6AxZxViZEh5/Tokens?node-id=12491%3A946
 */
export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  {variant, className: extraClassName, ...restProps},
  ref,
) {
  const level = usePrivateHeadingLevel();
  const tag = `h${Math.min(level, 6)}` as TextProps['tag'];

  return (
    <Text
      ref={ref}
      variant={variant}
      tag={tag}
      className={classnames(styles.defaultColor, extraClassName)}
      // Eventually to be used for an accessibility runtime check
      {...(level > 6 ? {'data-excessive-heading-level': level} : {})}
      {...restProps}
    />
  );
});
